import { cloudflareTransform, removeHTMLfromString } from "../../../helpers";
import type { TTarget } from "../../../types";
import Link from "../link/link";

type TContentType =
  | "paragraph"
  | "heading-two"
  | "heading-three"
  | "heading-four"
  | "list-item"
  | "image"
  | "link"
  | "bulleted-list"
  | "numbered-list"
  | "list-item";

type TChildTextItem = {
  text: string;
};

type TContentChild = {
  type?: TContentType;
  text: string;
  italic?: boolean;
  bold?: boolean;
  url?: string;
  target?: TTarget;
  width?: number;
  height?: number;
  alt?: string;
  children?: Array<TChildTextItem>;
};

export type TContent = {
  type: string;
  children?: Array<{
    type?: TContentType;
    children?: Array<TContentChild>;
  }>;
};

type TContentRendererProps = {
  content?: TContent;
  className?: string;
};

const getParagrahpContent = (children: Array<TContentChild>) => {
  const content = children.map((c: TContentChild, i: number) => {
    if (c.italic) {
      return <em key={i}>{removeHTMLfromString(c.text)}</em>;
    } else if (c.bold) {
      return <strong key={i}>{removeHTMLfromString(c.text)}</strong>;
    } else if (c.type === "link") {
      const linkTitle = c?.children?.map((cc: TChildTextItem) => {
        return cc.text;
      });

      return linkTitle ? (
        <Link url={c.url} title={removeHTMLfromString(linkTitle.toString())} target={c.target} key={i} />
      ) : null;
    } else if (c.type === "image") {
      return <img src={cloudflareTransform(c.url)} alt={c.alt} width={c.width} height={c.height} key={i} />;
    } else if (c.type === "list-item") {
      return c.children?.map((item: TChildTextItem, i: number) => (
        <li key={item.text + i}>{removeHTMLfromString(item.text)}</li>
      ));
    } else {
      return removeHTMLfromString(c.text);
    }
  });

  return content;
};

const getHeaderText = (children: Array<TContentChild>) => {
  const headingText = children.map((c: TContentChild) => {
    let txt = [];
    if (c.text) {
      txt.push(c.text);
    } else if (c.children) {
      c.children.forEach((cc: TChildTextItem) => {
        txt.push(cc.text);
      });
    }
    return removeHTMLfromString(txt.join(" "));
  });
  return headingText;
};

const ContentRenderer = ({ className, content }: TContentRendererProps) => {
  if (!content) {
    return null;
  }

  return (
    <div className={[className, "html-content"].join(" ")}>
      {content?.children?.map(
        (
          child: {
            type?: TContentType;
            children?: Array<TContentChild>;
          },
          i: number
        ) => {
          const secondLevelChildren = child.children;
          if (secondLevelChildren) {
            switch (child.type) {
              case "paragraph":
                return <p key={child.type + i}>{getParagrahpContent(secondLevelChildren)}</p>;
              case "heading-two":
                return <h2 key={child.type + i}>{getHeaderText(secondLevelChildren)}</h2>;
              case "heading-three":
                return <h3 key={child.type + i}>{getHeaderText(secondLevelChildren)}</h3>;
              case "heading-four":
                return <h4 key={child.type + i}>{getHeaderText(secondLevelChildren)}</h4>;
              case "bulleted-list":
                return <ul key={child.type + i}>{getParagrahpContent(secondLevelChildren)}</ul>;
              case "numbered-list":
                return <ol key={child.type + i}>{getParagrahpContent(secondLevelChildren)}</ol>;
            }
          }
        }
      )}
    </div>
  );
};

export default ContentRenderer;
